import { gql } from '@reverbdotcom/commons/src/gql';
import { compact } from 'lodash';
import { arbiter_AuctionType } from '@reverbdotcom/commons/src/gql/graphql';
import { canViewFinancingOptions, isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import { userContextIsTrackable } from '@reverbdotcom/commons/src/elog/mparticle';
import {
  localizeItemRegionFilter,
  applyProximityBoost,
} from '@reverbdotcom/commons/src/seller_location_experiments';
import experiments from '@reverbdotcom/commons/src/experiments';
import { type ICSPLayoutProps } from '../csp_layout';
import { IUser } from '@reverbdotcom/commons';

/* This query is server side rendered, only add things to this query that are 100% necessary and visible to the user on initial page load **/
export const cspLayoutQuery = gql(`
  query Core_Csp_LayoutQuery(
    $auctionType: arbiter_AuctionType
    $canonicalFinish: String
    $cspSlug: String
    $hfid: String
    $hfidPresent: Boolean!
    $includeFullFinancingFields: Boolean!
    $itemRegion: String
    $shippingRegionCode: String
    $shouldSkipTracking: Boolean!
    $showListingLocation: Boolean!
    $traitValues: [String]
    $userCurrency: String
    $useNewBreadcrumbs: Boolean!
    $applyProximityBoost: Boolean!
  ) {
    csp(
      input: {slug: $cspSlug}
    ) {
      _id
      id
      title
      summary
      body
      metaDescription
      model
      generic
      finishes
      brand {
        _id
        name
        slug
        webLink {
          href
        }
      }
      categories {
        _id
        id
        name
        slug
        root
        rootSlug
        webLink {
          href
        }
        cmsUrl {
          href
        }
        parentCategories {
          _id
          id
          name
          slug
          rootSlug
          webLink {
            href
          }
          cmsUrl {
            href
          }
        }
      }
      videos {
        videoLinks {
          youtubeLink
          youtubeVideoId
        }
      }
      specs {
        productSpecs {
          _id
          type
          name
          path
          value
        }
      }
      breadcrumbs @include(if: $useNewBreadcrumbs) {
        title
        url
      }
      supersize_images: images(input:{
        transform: "supersize"
        scope: "photos"
        limit: 20
      }) {
        _id
        source
      }
      large_images: images(input:{
        transform: "large"
        scope: "photos"
        limit: 20
      }) {
        _id
        source
      }
      hero_image: images(input:{
        transform: "banner_wide"
        scope: "hero_image"
        limit: 1
      }) {
        _id
        source
      }
      cspImage: images(input: {
        transform: "card_square"
        scope: "photos"
        limit: 1
      }) {
        _id
        source
      }
      canonicalProducts {
        _id
        id
        name
      }
      ... CSPAdditionalTrackingFields
      ... CSPReviewJumplinksData
      ... FeaturedListingCSP
      ...SellOrTradeInCTAData
    }
    featuredListing: featuredListing(
      input: {
        cspSlug: $cspSlug
        userShippingRegion: $shippingRegionCode
        canonicalFinish: $canonicalFinish
        traitValues: $traitValues
        itemRegion: $itemRegion
        auctionType: $auctionType
        userCurrency: $userCurrency
        applyProximityBoost: $applyProximityBoost
      }
    ) {
      listing {
        _id
        ... ListingData
        ... ListingsCollection
        ... mParticleListingFields @skip(if: $shouldSkipTracking)
        ... FullFinancingFields @include(if: $includeFullFinancingFields)
        ... ShopFields @include(if: $showListingLocation)
        ... BumpKey
        ... FeaturedListingWithPrice
      }
    }
    hfid: listing(
      input: {
        id: $hfid
      }
    ) @include(if: $hfidPresent) {
      _id
      ... ListingData
      ... ListingsCollection
      ... mParticleListingFields @skip(if: $shouldSkipTracking)
      ... FullFinancingFields @include(if: $includeFullFinancingFields)
      ... BumpKey
      ... FeaturedListingWithPrice
    }
  }
`);

export const getLayoutQueryVariables = ({
  location,
  params,
  user,
}: ICSPLayoutProps & {
  user: Partial<IUser>,
} ) => ({
  auctionType: arbiter_AuctionType.CSP,
  canonicalFinish: (location?.query?.canonicalFinish || ''),
  canonicalFinishes: compact([location?.query?.canonicalFinish]),
  cspSlug: params.slug,
  hfid: (location?.query?.hfid || ''),
  hfidPresent: !!location?.query?.hfid,
  includeFullFinancingFields: canViewFinancingOptions(user),
  applyProximityBoost: applyProximityBoost(user),
  shippingRegionCode: user?.shippingRegionCode,
  shouldSkipTracking: !userContextIsTrackable(user),
  showListingLocation: isExperimentEnabled(user, experiments.LISTING_CARD_LOCATION),
  ...localizeItemRegionFilter(location?.query?.item_region, user),
  traitValues: compact([(location?.query?.trait_values || '')]).flat(),
  userCurrency: user?.currency,
  useNewBreadcrumbs: user?.locale === 'en', // new breadcrumbs use browse pages which currently don't have translations
});
